/**
 * Schémas de validation consolidés
 * Centralise toutes les validations Zod pour éviter la duplication
 */

import { z } from 'zod';
import { validateProfessionalEmail, validateNotSelfReferencing, quickValidateProfessionalEmail } from '@/utils/emailValidation';

// ============================================================================
// TYPES DE BASE
// ============================================================================

export const relationTypes = [
  { value: 'manager', label: 'Manager' },
  { value: 'collègue', label: 'Collègue' },
  { value: 'client', label: 'Client' },
  { value: 'autre', label: 'Autre' },
] as const;

export type RelationType = typeof relationTypes[number]['value'];

// ============================================================================
// TYPES VALIDATION EMAIL AVANCÉE
// ============================================================================

export type EmailCheckStatus = 'professional' | 'personal' | 'disposable' | 'invalid';
export type EmailValidationLevel = 'block' | 'warning' | 'ok';

export interface EmailValidationResult {
  isValid: boolean;
  isProfessional: boolean;
  level: EmailValidationLevel;
  reason: string;
  emailCheckStatus: EmailCheckStatus;
  emailCheckDescription: string;
  coherenceScore?: number;
}

// ============================================================================
// SCHÉMAS DE VALIDATION RÉFÉRENTS
// ============================================================================

/**
 * Schéma de base pour validation rapide (sans validations coûteuses)
 * Utilisé pour la validation en temps réel dans les formulaires
 */
export const quickReferentSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  email: z.string()
    .email('Email invalide')
    .refine((email) => quickValidateProfessionalEmail(email), {
      message: 'Veuillez utiliser une adresse email professionnelle'
    }),
  phone: z.string().optional(),
  position: z.string().min(1, 'Le poste est requis'),
  relation_type: z.enum(['manager', 'collègue', 'client', 'autre']),
  // Nouvelles colonnes pour les dates de collaboration candidat
  candidate_collaboration_start_date: z.string().optional(),
  candidate_collaboration_end_date: z.string().optional(),
  candidate_still_working_together: z.boolean().optional(),
});

/**
 * Schéma complet pour validation finale (avec validations coûteuses)
 * Utilisé lors de la soumission finale des formulaires
 */
export const referentSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  email: z.string()
    .email('Email invalide')
    .refine((email) => validateProfessionalEmail(email), {
      message: 'Veuillez utiliser une adresse email professionnelle (pas Gmail, Yahoo, Hotmail, etc.)'
    }),
  phone: z.string().optional(),
  position: z.string().min(1, 'Le poste est requis'),
  relation_type: z.enum(['manager', 'collègue', 'client', 'autre']),
  // Nouvelles colonnes pour les dates de collaboration candidat
  candidate_collaboration_start_date: z.string().optional(),
  candidate_collaboration_end_date: z.string().optional(),
  candidate_still_working_together: z.boolean().optional(),
}).refine((data) => {
  // Validation: si collaboration en cours, pas de date de fin
  if (data.candidate_still_working_together && data.candidate_collaboration_end_date) {
    return false;
  }
  // Validation: date de fin ne peut pas être antérieure à la date de début
  if (data.candidate_collaboration_start_date && data.candidate_collaboration_end_date) {
    return data.candidate_collaboration_end_date >= data.candidate_collaboration_start_date;
  }
  return true;
}, {
  message: 'Les dates de collaboration ne sont pas cohérentes',
  path: ['candidate_collaboration_end_date']
});

// ============================================================================
// SCHÉMAS DE VALIDATION CAMPAGNE
// ============================================================================

/**
 * Schéma pour les critères d'évaluation
 */
export const evaluationCriterionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string().optional(),
  is_custom: z.boolean().optional(),
  isAIGenerated: z.boolean().optional(),
  isCompliant: z.boolean().optional(),
  validationStatus: z.enum(['validated', 'validating', 'error', 'pending']).optional(),
  validationMessage: z.string().optional(),
  order: z.number().optional()
});

/**
 * Schéma pour les référents recommandés par l'IA
 */
export const recommendedReferentSchema = z.object({
  experienceId: z.string(),
  experienceRef: z.string(),
  suggestedRole: z.string(),
  reasoning: z.string(),
  priority: z.number(),
  isRecent: z.boolean(),
  validated: z.boolean().optional()
});

/**
 * Schéma de validation pour le formulaire de campagne
 * Consolidé depuis campaignForm.ts
 */
export const campaignSchema = z.object({
  candidate_name: z.string().optional(),
  candidate_email: z.string()
    .min(1, 'L\'email est requis')
    .email('Format d\'email invalide'),
  candidate_phone: z.string().optional(),
  target_position: z.string().optional(),
  // Documents
  candidate_cv_url: z.string().min(1, 'Le CV du candidat est requis'),
  job_description_file_url: z.string().optional(),
  job_description_text: z.string().optional(),
  // Référents recommandés par l'IA
  recommended_referents: z.array(recommendedReferentSchema).optional(),
  // Référents sélectionnés par l'utilisateur
  selected_referents: z.array(recommendedReferentSchema).optional(),
  // Critères d'évaluation personnalisés (5 minimum, 10 maximum)
  selected_evaluation_criteria: z.array(evaluationCriterionSchema)
    .min(5, 'Minimum 5 critères requis')
    .max(10, 'Maximum 10 critères autorisés'),
  // Configuration de campagne
  campaign_duration: z.enum(['3_days', '5_days', '1_week', '2_weeks', '1_month'], {
    required_error: 'La durée de campagne est requise'
  }),
  reminders_enabled: z.boolean().optional(),
}).refine((data) => {
  // Validation : au moins une description du poste (texte ou fichier)
  const hasText = !!data.job_description_text && data.job_description_text.trim().length >= 10;
  const hasFile = !!data.job_description_file_url && data.job_description_file_url.trim().length > 0;
  
  return hasText || hasFile;
}, {
  message: 'Veuillez fournir une description du poste (texte d\'au moins 10 caractères ou fichier)',
  path: ['job_description_text']
});

// ============================================================================
// SCHÉMAS COMPOSÉS ET CACHE
// ============================================================================

// Cache pour les schémas mémorisés
const schemaCache = new Map<string, z.ZodSchema>();

/**
 * Crée un schéma de validation pour la soumission de référents
 * avec validation anti-auto-référencement
 */
export const createSubmitReferentsSchema = (candidateEmail: string) => {
  const cacheKey = `submit-referents-${candidateEmail}`;
  
  if (schemaCache.has(cacheKey)) {
    const cachedSchema = schemaCache.get(cacheKey);
    if (cachedSchema) return cachedSchema;
  }

  const schema = z.object({
    referents: z.array(referentSchema)
      .min(1, 'Au moins un référent est requis')
      .refine((referents) => {
        // Vérifier qu'aucun référent n'utilise l'email du candidat
        return referents.every(referent => 
          validateNotSelfReferencing(referent.email, candidateEmail)
        );
      }, {
        message: 'Vous ne pouvez pas vous désigner comme votre propre référent'
      })
      .refine((referents) => {
        // Vérifier l'unicité des emails
        const emails = referents.map(r => r.email.toLowerCase());
        return new Set(emails).size === emails.length;
      }, {
        message: 'Chaque référent doit avoir une adresse email différente'
      })
  });

  schemaCache.set(cacheKey, schema);
  return schema;
};

/**
 * Schéma rapide pour validation en temps réel (sans validations coûteuses)
 */
export const createQuickSubmitReferentsSchema = () => {
  const cacheKey = 'quick-submit-referents';
  
  if (schemaCache.has(cacheKey)) {
    const cachedSchema = schemaCache.get(cacheKey);
    if (cachedSchema) return cachedSchema;
  }

  const schema = z.object({
    referents: z.array(quickReferentSchema).min(1, 'Au moins un référent est requis'),
  });

  schemaCache.set(cacheKey, schema);
  return schema;
};

/**
 * Schéma simple pour la soumission de référents
 */
export const submitReferentsSchema = z.object({
  referents: z.array(referentSchema).min(1, 'Au moins un référent est requis'),
});

// ============================================================================
// TYPES INFÉRÉS
// ============================================================================

export type CampaignFormData = z.infer<typeof campaignSchema>;
export type SubmitReferentsFormData = z.infer<typeof submitReferentsSchema>;
export type ReferentFormData = z.infer<typeof referentSchema>;
export type QuickReferentFormData = z.infer<typeof quickReferentSchema>;
export type EvaluationCriterionData = z.infer<typeof evaluationCriterionSchema>;
export type RecommendedReferentData = z.infer<typeof recommendedReferentSchema>;

// ============================================================================
// VALEURS PAR DÉFAUT
// ============================================================================

export const defaultCampaignFormValues: Partial<CampaignFormData> = {
  candidate_name: '',
  candidate_email: '',
  target_position: '',
  candidate_cv_url: '',
  job_description_file_url: '',
  job_description_text: '',
  recommended_referents: [],
  selected_referents: [],
  selected_evaluation_criteria: [],
  campaign_duration: '3_days', // Valeur par défaut au lieu d'undefined
  reminders_enabled: true,
};

/**
 * Utilitaire pour nettoyer le cache des schémas
 * Utile pour les tests ou le rechargement à chaud
 */
export const clearSchemaCache = () => {
  schemaCache.clear();
};

/**
 * Utilitaire pour obtenir la taille du cache
 * Utile pour le monitoring des performances
 */
export const getSchemaCacheSize = () => {
  return schemaCache.size;
};